import { useMemo } from 'react'
import { useAuth } from '../../../../../../../auth'
import { TableState } from '../../../../../../../hooks/use-table-state'
import { UserPermissions } from '../../../../../../../types/permissions'
import { RouteMode } from '../../../../../../../types/route'
import { FormElementConfig } from '../../../../../../../forms/types'
import { toDollarCurrencyFormat } from '../../../../../../../forms/utils'
import { ApplicationDetailsFields } from './config'
import { ColumnHeader } from '@msaf/core-react'
import { FENCING_TABLE_COLUMNS } from '../../../../../../../forms/constants'

interface FencingConfigProps extends Pick<TableState, 'refreshTable' | 'deleteAction'> {
  mode?: RouteMode
  isMigrated?: boolean
  planId?: string
  applicationId?: string
}

export const useFencingConfig = ({
  mode,
  refreshTable,
  isMigrated,
  planId,
  applicationId,
  deleteAction,
}: FencingConfigProps): FormElementConfig<ApplicationDetailsFields>[] => {
  const { user } = useAuth()
  return useMemo(() => {
    if (isMigrated)
      return [
        {
          type: 'field',
          element: {
            type: 'repeating-section',
            heading: 'Fencing',
            sectionName: 'Fencing action',
            repeating: true,
            fieldId: 'fencingActions',
            fields: [
              {
                type: 'lookup',
                fieldId: 'type',
                lookupId: 'action_subtype',
                fieldFilters: { feature_category: 'fencing' },
              },
              {
                type: 'number',
                fieldId: 'length',
              },
              {
                fieldId: 'fenceType',
                type: 'lookup',
                lookupId: 'fence_type',
              },
              {
                type: 'number',
                fieldId: 'proposedCost',
              },
            ],
          },
        },
      ]

    return [
      {
        type: 'atom',
        element: { type: 'heading', level: 2, content: 'Fencing' },
      },
      {
        type: 'field',
        element: {
          type: 'table',
          currentUserPermissions: user?.permissions,
          columnHeaders: [
            ...FENCING_TABLE_COLUMNS,
            {
              elementKey: 'actions',
              columnHeading: '',
              viewColumn: 'actions',
              type: 'actions-no-data',
              actions:
                mode == RouteMode.EDIT
                  ? [
                      {
                        label: 'Delete',
                        type: 'deleteAction',
                        args: [
                          {
                            elementKey: 'id',
                          },
                        ],
                        actionPermissions: [UserPermissions.EDIT_PLANS],
                      },
                    ]
                  : [],
            },
          ] as ColumnHeader[],
          refreshTable: refreshTable,
          tableActionHandlers: {
            deleteAction,
          },
          defaultSortBy: {
            orderColumn: 'locationName',
            orderDirection: 'desc',
          },
          requestUrl: `/api/plan/${planId}/project/${applicationId}/feature/fencing`,
          rowClickAction: {
            label: 'View',
            type: 'transitionAction',
            args: [{ constant: `plans/${mode}/${planId}/plan/project/${applicationId}/action` }, { elementKey: 'id' }],
          },
          noResultsMessage: 'No fencing actions added',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.fencingLength',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.fencingCost',
          conversion: toDollarCurrencyFormat,
        },
      },
    ]
  }, [mode, refreshTable, isMigrated, planId, applicationId, deleteAction])
}
